import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { AxiosError } from 'axios'

import { axiosInstance } from '../helpers'
import {
    PropertyEvent,
    CreatePropertyEventInput,
    UpdatePropertyEventInput,
    DraftMessageResponse,
    SendEventMessageInput,
} from '../models/PropertyEvent'
import { TenantMessage } from '../models/TenantMessage'
import { useUser } from './useUser'

const EVENTS_KEY = 'property-events'
const BASE = '/beta/api/ezos-calendar/property-events'

const extractError = (error: unknown, fallback: string): string => {
    const data = (error as AxiosError)?.response?.data as
        | Record<string, unknown>
        | undefined

    if (data) {
        const nonField = data.non_field_errors
        if (Array.isArray(nonField) && nonField.length > 0) {
            return String(nonField[0])
        }
        const firstKey = Object.keys(data)[0]
        if (firstKey) {
            const val = data[firstKey]
            if (Array.isArray(val) && val.length > 0) {
                return `${firstKey}: ${val[0]}`
            }
            if (typeof val === 'string') {
                return val
            }
        }
    }

    return fallback
}

interface UsePropertyEventsParams {
    startsAfter?: string
    startsBefore?: string
}

export const usePropertyEvents = (params?: UsePropertyEventsParams) => {
    const queryClient = useQueryClient()
    const { workspaceUser } = useUser()

    const apartmentId = workspaceUser?.active_workspace?.id
    const isApartment =
        workspaceUser?.active_workspace?.company_type === 'APARTMENT'

    const { data: events, isLoading, error, refetch } = useQuery<
        PropertyEvent[],
        Error
    >({
        queryKey: [
            EVENTS_KEY,
            apartmentId,
            params?.startsAfter,
            params?.startsBefore,
        ],
        queryFn: async () => {
            const queryParams = new URLSearchParams()
            if (params?.startsAfter) {
                queryParams.set('starts_after', params.startsAfter)
            }
            if (params?.startsBefore) {
                queryParams.set('starts_before', params.startsBefore)
            }
            const url = queryParams.toString()
                ? `${BASE}/?${queryParams.toString()}`
                : `${BASE}/`
            const res = await axiosInstance.get(url)
            const data = res.data
            if (Array.isArray(data)) return data
            if (data && Array.isArray(data.results)) return data.results
            return []
        },
        enabled: isApartment && !!apartmentId,
        keepPreviousData: true,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    const invalidate = () =>
        queryClient.invalidateQueries({ queryKey: [EVENTS_KEY] })

    const createEvent = useMutation({
        mutationFn: async (input: CreatePropertyEventInput) => {
            const { attachments, ...fields } = input

            if (attachments && attachments.length > 0) {
                const formData = new FormData()
                formData.append('title', fields.title)
                formData.append('starts_at', fields.starts_at)
                formData.append('ends_at', fields.ends_at)
                if (fields.description) {
                    formData.append('description', fields.description)
                }
                if (fields.location) {
                    formData.append('location', fields.location)
                }
                if (fields.is_all_day !== undefined) {
                    formData.append('is_all_day', String(fields.is_all_day))
                }
                if (fields.status) {
                    formData.append('status', fields.status)
                }
                attachments.forEach((file) => {
                    formData.append('attachments', file)
                })

                const res = await axiosInstance.post<PropertyEvent>(
                    `${BASE}/`,
                    formData,
                    { headers: { 'Content-Type': 'multipart/form-data' } },
                )
                return res.data
            }

            const res = await axiosInstance.post<PropertyEvent>(
                `${BASE}/`,
                fields,
            )
            return res.data
        },
        onSuccess: () => {
            invalidate()
            toast.success('Event created')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to create event'))
        },
    })

    const updateEvent = useMutation({
        mutationFn: async ({
            id,
            ...input
        }: UpdatePropertyEventInput & { id: number }) => {
            const { attachments, remove_attachment_ids, ...fields } = input

            const hasFiles = attachments && attachments.length > 0
            const hasRemovals =
                remove_attachment_ids && remove_attachment_ids.length > 0

            if (hasFiles || hasRemovals) {
                const formData = new FormData()
                Object.entries(fields).forEach(([key, value]) => {
                    if (value !== undefined) {
                        formData.append(key, String(value))
                    }
                })
                if (hasRemovals) {
                    formData.append(
                        'remove_attachment_ids',
                        JSON.stringify(remove_attachment_ids),
                    )
                }
                if (hasFiles) {
                    attachments!.forEach((file) => {
                        formData.append('attachments', file)
                    })
                }

                const res = await axiosInstance.patch<PropertyEvent>(
                    `${BASE}/${id}/`,
                    formData,
                    { headers: { 'Content-Type': 'multipart/form-data' } },
                )
                return res.data
            }

            const res = await axiosInstance.patch<PropertyEvent>(
                `${BASE}/${id}/`,
                fields,
            )
            return res.data
        },
        onSuccess: () => {
            invalidate()
            toast.success('Event updated')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to update event'))
        },
    })

    const deleteEvent = useMutation({
        mutationFn: async (id: number) => {
            await axiosInstance.delete(`${BASE}/${id}/`)
        },
        onSuccess: () => {
            invalidate()
            toast.success('Event deleted')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to delete event'))
        },
    })

    const draftMessage = useMutation({
        mutationFn: async (id: number) => {
            const res = await axiosInstance.get<DraftMessageResponse>(
                `${BASE}/${id}/draft-message/`,
            )
            return res.data
        },
        onError: (err) => {
            const status = (err as AxiosError)?.response?.status
            if (status === 503 || status === 502) {
                toast.error(
                    "Lori couldn't generate a draft right now. Please try again.",
                )
                return
            }
            toast.error(extractError(err, "Lori couldn't generate a draft"))
        },
    })

    const sendEventMessage = useMutation({
        mutationFn: async ({
            id,
            ...input
        }: SendEventMessageInput & { id: number }) => {
            const { attachments, ...fields } = input

            if (attachments && attachments.length > 0) {
                const formData = new FormData()
                formData.append('subject', fields.subject)
                formData.append('body', fields.body)
                formData.append('audience_type', fields.audience_type)
                if (fields.lease_scope) {
                    formData.append('lease_scope', fields.lease_scope)
                }
                if (fields.client_token) {
                    formData.append('client_token', fields.client_token)
                }
                if (fields.send_email !== undefined) {
                    formData.append('send_email', String(fields.send_email))
                }
                if (fields.send_sms !== undefined) {
                    formData.append('send_sms', String(fields.send_sms))
                }
                if (fields.email_sender_name) {
                    formData.append(
                        'email_sender_name',
                        fields.email_sender_name,
                    )
                }
                if (fields.email_reply_to) {
                    formData.append('email_reply_to', fields.email_reply_to)
                }
                if (fields.lease_ids.length > 0) {
                    formData.append(
                        'lease_ids',
                        JSON.stringify(fields.lease_ids),
                    )
                }
                attachments.forEach((file) => {
                    formData.append('attachments', file)
                })

                const res = await axiosInstance.post<TenantMessage>(
                    `${BASE}/${id}/send-message/`,
                    formData,
                    { headers: { 'Content-Type': 'multipart/form-data' } },
                )
                return res.data
            }

            const res = await axiosInstance.post<TenantMessage>(
                `${BASE}/${id}/send-message/`,
                fields,
            )
            return res.data
        },
        onSuccess: () => {
            invalidate()
            toast.success('Announcement sent')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to send announcement'))
        },
    })

    return {
        events: events ?? [],
        isLoading,
        error,
        refetch,
        isApartment,
        createEvent,
        updateEvent,
        deleteEvent,
        draftMessage,
        sendEventMessage,
    }
}
